import { useState } from 'react';
import { Link } from 'react-router';

import type { UserResponse } from '../../api';
import {
  AsyncSection,
  DataTable,
  ErrorState,
  PageHeader,
  Panel,
  useNotifications,
  type Column,
} from '../../components';
import { PATHS } from '../../routes/paths';
import { useCompanies, useDeactivateUser, useGroups, useUsers } from '../queries';
import { USERS_COPY, deactivationQuestion, roleLabel, userBelonging } from './staffUsers';

/**
 * Who can sign in, as vyttech staff read it.
 *
 * Next door to [`StaffCredentialsPage`](./StaffCredentialsPage.tsx) and
 * deliberately not the same screen: a machine credential and a person are
 * created for different reasons, by different hands, and retired on different
 * days.
 *
 * Creating somebody has moved to [`NewStaffUserPage`](./NewStaffUserPage.tsx),
 * behind the action in the header. What is left here is the question a menu
 * entry should answer -- who has an account, in which company or company group,
 * and is it still live -- plus the one destructive action that belongs to a row.
 *
 * **A company and a group are two filters, not one.** A person registered at a
 * group (#391) belongs to no company, so the company filter -- the scope
 * header -- never shows them; the group filter is a query parameter on the
 * unscoped list instead. Choosing one clears the other, because together they
 * would always answer "nobody".
 *
 * **Deactivation is not deletion.** A deactivated account stops signing in and
 * its existing token dies on its next request, but the row stays on the list,
 * marked -- "who could sign in, and until when" is a question that gets asked
 * after the fact. It is also the one action here that asks first, because a
 * person locked out mid-shift finds out by being locked out.
 *
 * Writing anything here needs `user:write`, which among the human roles only
 * `vyttech_admin` holds. An analyst can read the list and will be refused on
 * the way out; that refusal is rendered where the action was, through the same
 * error components as everything else, rather than being swallowed.
 */
export function StaffUsersPage() {
  const companies = useCompanies();
  const { notify } = useNotifications();

  // `null` is "every user this caller can see", which is what the endpoint
  // answers without a scope header -- not a company whose id we happen not to
  // know yet. Hence null rather than ''.
  const [filterCompanyId, setFilterCompanyId] = useState<string | null>(null);
  const [filterGroupId, setFilterGroupId] = useState<string | null>(null);
  const users = useUsers(filterCompanyId, filterGroupId);
  const groups = useGroups();
  const deactivate = useDeactivateUser();

  const companyItems = companies.data?.items ?? [];
  const groupItems = groups.data?.items ?? [];

  const columns: readonly Column<UserResponse>[] = [
    { key: 'full_name', header: USERS_COPY.nameLabel, render: (user) => user.full_name },
    { key: 'email', header: USERS_COPY.emailLabel, render: (user) => user.email },
    { key: 'role', header: USERS_COPY.profileLabel, render: (user) => roleLabel(user.role) },
    {
      key: 'company',
      header: USERS_COPY.belongingLabel,
      render: (user) => {
        const belonging = userBelonging(user, companyItems, groupItems);
        if (belonging.kind !== 'group') {
          return belonging.name;
        }
        return (
          <>
            <span className="badge badge--muted">{USERS_COPY.groupBadge}</span> {belonging.name}
          </>
        );
      },
    },
    {
      key: 'is_active',
      header: USERS_COPY.statusLabel,
      render: (user) =>
        user.is_active ? (
          USERS_COPY.active
        ) : (
          // Still on the list, and said plainly. A row that merely disappeared
          // would leave "was this person removed, or did I imagine them?" as a
          // question nobody can answer.
          <span className="table__muted">{USERS_COPY.inactive}</span>
        ),
    },
    {
      key: 'actions',
      header: 'Ações',
      align: 'end',
      render: (user) => {
        if (!user.is_active) {
          return <span className="table__muted">—</span>;
        }
        return (
          <button
            type="button"
            className="button button--danger"
            disabled={deactivate.isPending}
            onClick={() => {
              // Desativar tira a pessoa do ar na próxima requisição dela, então
              // a tela pergunta antes -- como a revogação de credencial faz.
              if (!window.confirm(deactivationQuestion(user.email))) {
                return;
              }
              deactivate.mutate(
                { userId: user.id, companyId: user.company_id },
                {
                  onSuccess: () => notify('success', USERS_COPY.deactivated),
                },
              );
            }}
          >
            {USERS_COPY.deactivate}
          </button>
        );
      },
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title={USERS_COPY.title}
        description={USERS_COPY.description}
        actions={
          <Link className="button button--primary" to={PATHS.staffUserNew}>
            {USERS_COPY.addPerson}
          </Link>
        }
      />

      <Panel title={USERS_COPY.listTitle} description={USERS_COPY.listDescription}>
        <div className="field">
          <label className="field__label" htmlFor="users-company-filter">
            Filtrar por empresa
          </label>
          <p className="field__hint" id="users-company-filter-hint">
            Escolher uma empresa mostra só as pessoas dela — a equipe vyttech, que não pertence a
            nenhuma, sai da lista.
          </p>
          <select
            id="users-company-filter"
            className="field__input"
            aria-describedby="users-company-filter-hint"
            value={filterCompanyId ?? ''}
            onChange={(event) => {
              setFilterCompanyId(event.target.value === '' ? null : event.target.value);
              setFilterGroupId(null);
            }}
          >
            <option value="">Todas as empresas</option>
            {companyItems.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label className="field__label" htmlFor="users-group-filter">
            Filtrar por grupo
          </label>
          <p className="field__hint" id="users-group-filter-hint">
            Mostra só as pessoas cadastradas no grupo, que atuam pelas empresas dele. Quem está
            cadastrado numa empresa do grupo aparece no filtro por empresa.
          </p>
          <select
            id="users-group-filter"
            className="field__input"
            aria-describedby="users-group-filter-hint"
            value={filterGroupId ?? ''}
            onChange={(event) => {
              setFilterGroupId(event.target.value === '' ? null : event.target.value);
              setFilterCompanyId(null);
            }}
          >
            <option value="">Todos os grupos</option>
            {groupItems.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>

        <AsyncSection
          state={users}
          loadingLabel="Carregando pessoas…"
          errorTitle="Não foi possível carregar as pessoas"
          isEmpty={(data) => data.items.length === 0}
          emptyTitle="Nenhuma pessoa cadastrada"
          emptyDescription="Ninguém entra no produto dentro deste filtro ainda."
        >
          {(data) => (
            <DataTable
              id="staff-users"
              caption="Pessoas que entram no produto"
              columns={columns}
              rows={data.items}
              rowKey={(user) => user.id}
            />
          )}
        </AsyncSection>

        {/* Uma desativação recusada não tem onde cair dentro da tabela, e some
            se for só um toast. Fica aqui, abaixo da lista onde a ação foi
            pedida, com o código de referência que o suporte pede. */}
        {deactivate.isError && (
          <ErrorState error={deactivate.error} title={USERS_COPY.deactivateRefused} />
        )}
      </Panel>
    </div>
  );
}
